import { computed, makeObservable } from 'mobx';

import { getPlainText } from '@gem/modules/markdown';

import {
  IMessageRequestQuotedMessageData,
  TQuoteSource,
} from 'APP/model/message/messageModel.types';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';
import { AlbumPayload } from 'STORE/Messages/Message/Payload/AlbumPayload/AlbumPayload';
import { User } from 'STORE/Users/User/User';

interface IQuoteParams {
  text: string;
  source: TQuoteSource;
}

export class Quote {
  text: string;
  source: TQuoteSource;

  constructor(params: IQuoteParams) {
    makeObservable(this);
    this.text = params.text;
    this.source = params.source;
  }

  @computed
  get senderId(): string | null {
    return (
      this.source.user?.userId ||
      this.source.userChat?.user.userId ||
      this.source.unknown?.userId ||
      null
    );
  }

  @computed
  get groupId(): string | null {
    return (
      this.source.chat?.chatId ||
      this.source.userChat?.chat.chatId ||
      this.source.unknown?.chatId ||
      null
    );
  }

  @computed
  get messageId(): string | null {
    return (
      this.source.chat?.messageId ||
      this.source.userChat?.chat.messageId ||
      this.source.unknown?.messageId ||
      null
    );
  }

  @computed
  get senderUser(): User | null {
    if (!this.senderId) {
      return null;
    }
    return Entities.usersStore.getUserById(this.senderId);
  }

  @computed
  get group(): Group | null {
    if (!this.groupId) {
      return null;
    }
    return Entities.GroupsStore.getGroupById(this.groupId);
  }

  @computed
  get message(): ChatMessage | null {
    if (!this.messageId || !this.group) {
      return null;
    }
    return this.group.messagesStore.getMessageById(this.messageId);
  }

  @computed
  get senderName(): string | null {
    return this.senderUser?.avatarTitle || null;
  }

  @computed
  get groupTitle(): string | null {
    if (!this.group || this.group.isP2P) {
      return this.source.chat?.title || this.source.userChat?.chat?.title || null;
    }
    return this.group.title;
  }

  @computed
  get currentText(): string {
    if (!this.message?.renderPayload) {
      return '';
    }

    if (this.message.renderPayload instanceof AlbumPayload) {
      return this.message.renderPayload.comment || '';
    }

    return this.message.renderPayload.text || this.message.renderPayload.comment || '';
  }

  @computed
  get isTextStillValid(): boolean {
    if (!this.message) {
      return true;
    }

    if (!this.currentText) {
      return false;
    }

    const plainText = getPlainText(this.currentText);

    return plainText.includes(this.text);
  }

  toJSON(): IMessageRequestQuotedMessageData {
    return {
      text: this.text,
      sourceChatId: this.groupId || '',
      sourceMessageId: this.messageId || '',
    };
  }
}
